import googleConfig from './GoogleConfig';

// GoogleService.js - Updated to use Google Identity Services (GIS)
class GoogleService {
  constructor() {
    this.isInitialized = false;
    this.config = googleConfig;
    this.currentUser = null;
    this.code = null;
    this.idToken = null;
  }

  // Khởi tạo Google Identity Services
  async init(config = {}) {
    if (this.isInitialized) {
      return Promise.resolve();
    }

    // Merge config với config mặc định
    this.config = { ...this.config, ...config };

    // Validate required config
    if (!this.config.clientId) {
      throw new Error('clientId là bắt buộc');
    }

    return new Promise((resolve, reject) => {
      // Kiểm tra nếu Google Identity Services đã được load
      if (typeof window !== 'undefined' && window.google?.accounts?.id) {
        this.initializeGIS().then(resolve).catch(reject);
        return;
      }

      // Load Google Identity Services
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.initializeGIS().then(resolve).catch(reject);
      };
      script.onerror = () =>
        reject(new Error('Không thể tải Google Identity Services'));

      // Kiểm tra nếu script đã tồn tại
      const existingScript = document.querySelector(
        'script[src="https://accounts.google.com/gsi/client"]'
      );
      if (!existingScript) {
        document.head.appendChild(script);
      } else {
        // Nếu script đã tồn tại, chờ nó load xong
        if (window.google?.accounts?.id) {
          this.initializeGIS().then(resolve).catch(reject);
        } else {
          existingScript.onload = () => {
            this.initializeGIS().then(resolve).catch(reject);
          };
        }
      }
    });
  }

  // Initialize Google Identity Services
  initializeGIS() {
    return new Promise((resolve, reject) => {
      try {
        // console.log("Khởi tạo Google Identity Services với config:", {
        //   clientId: this.config.clientId
        //     ? this.config.clientId.substring(0, 20) + "..."
        //     : "MISSING",
        // });

        // Check browser compatibility
        const isCompatibleBrowser = this.checkBrowserCompatibility();

        const initConfig = {
          client_id: this.config.clientId,
          callback: () => {},
          auto_select: false,
          cancel_on_tap_outside: true,
          // Enable FedCM as Google is making it mandatory
          use_fedcm_for_prompt: true,
          itp_support: true,
        };

        // For older browsers or those with strict privacy settings
        if (!isCompatibleBrowser) {
          initConfig.use_fedcm_for_prompt = false;
          // console.warn(
          //   "Browser may not fully support modern GSI features, using fallback configuration"
          // );
        }

        // Initialize Google Identity Services
        window.google.accounts.id.initialize(initConfig);

        // Initialize OAuth 2.0 flow for additional scopes if needed
        if (this.config.scopes && this.config.scopes.length > 0) {
          this.initializeOAuth2();
        }

        this.isInitialized = true;
        // console.log("Google Identity Services đã được khởi tạo thành công");
        resolve();
      } catch (error) {
        // console.error("Lỗi khởi tạo Google Identity Services:", error);
        reject(new Error(`Khởi tạo thất bại: ${error.message}`));
      }
    });
  }

  // Check browser compatibility for modern GSI features
  checkBrowserCompatibility() {
    try {
      // Check for required APIs
      const hasRequiredAPIs = !!(
        window.fetch &&
        window.Promise &&
        window.crypto &&
        window.crypto.subtle
      );

      // // Check for third-party cookie support (affects FedCM)
      // const hasThirdPartyCookies =
      //   !navigator.cookieEnabled || document.cookie.indexOf('test=') === -1;

      // // Check user agent for known problematic browsers
      // const userAgent = navigator.userAgent.toLowerCase();
      const isPrivateBrowsing = this.detectPrivateBrowsing();

      return hasRequiredAPIs && !isPrivateBrowsing;
    } catch (error) {
      console.warn('Could not determine browser compatibility:', error);
      return false;
    }
  }

  // Detect private browsing mode
  detectPrivateBrowsing() {
    try {
      // Test localStorage availability
      const testKey = '__test_private_browsing__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return false;
    } catch {
      return true;
    }
  }

  // Initialize OAuth 2.0 for additional scopes
  initializeOAuth2() {
    try {
      // console.log("Initializing OAuth2 with scopes:", scopeString);

      window.google.accounts.oauth2.initCodeClient({
        client_id: this.config.clientId,
        scope: this.config.scopes,
        callback: () => {},
      });
    } catch (error) {
      console.warn('Không thể khởi tạo OAuth2 client:', error);
    }
  }

  // // Handle credential response from Google Identity Services
  // handleCredentialResponse(response) {
  //   try {
  //     // Decode JWT token để lấy thông tin user
  //     const payload = JSON.parse(atob(response.credential.split(".")[1]));

  //     this.currentUser = {
  //       id: payload.sub,
  //       name: payload.name,
  //       givenName: payload.given_name,
  //       familyName: payload.family_name,
  //       email: payload.email,
  //       photo: payload.picture,
  //       emailVerified: payload.email_verified,
  //     };

  //     this.idToken = response.credential;
  //     // console.log("Đăng nhập thành công:", this.currentUser.name);

  //     // Trigger callback nếu có
  //     if (this.signInCallback) {
  //       this.signInCallback(this.currentUser);
  //     }
  //   } catch (error) {
  //     console.error("Lỗi xử lý credential response:", error);
  //   }
  // }

  // // Handle OAuth 2.0 response
  // handleOAuth2Response(response) {
  //   if (response.error) {
  //     console.error("OAuth2 Error:", response.error);
  //     return;
  //   }

  //   this.code = response.code;
  //   // console.log("Access token đã được lấy thành công");
  // }

  // Đăng nhập bằng popup
  async signIn() {
    if (!this.isInitialized) {
      throw new Error('GoogleService chưa được khởi tạo');
    }

    return new Promise((resolve, reject) => {
      try {
        // Set callback để handle response
        this.signInCallback = user => {
          this.signInCallback = null; // Clear callback
          resolve(user);
        };

        // Try different sign-in methods in order of preference
        this.trySignInMethods().then(resolve).catch(reject);
      } catch (error) {
        // console.error("Lỗi đăng nhập:", error);
        reject(new Error(`Đăng nhập thất bại: ${error.message}`));
      }
    });
  }

  // Try different sign-in methods to avoid FedCM issues
  async trySignInMethods() {
    return new Promise((resolve, reject) => {
      // Method 1: Try OAuth2 popup first (most reliable)
      // console.log("Attempting OAuth2 popup sign-in...");
      this.signInWithOAuth2Popup()
        .then(result => {
          // console.log("OAuth2 popup successful:", result);
          resolve(result);
        })
        .catch(oauth2Error => {
          console.log(
            'OAuth2 popup failed, trying direct OAuth2:',
            oauth2Error.message
          );

          // Method 2: Try direct OAuth2 without popup
          this.signInWithOAuth2()
            .then(result => {
              // console.log("Direct OAuth2 successful:", result);
              resolve(result);
            })
            .catch(directError => {
              console.log(
                'Direct OAuth2 failed, trying GSI:',
                directError.message
              );

              // Method 3: Try GSI as last resort
              this.tryGSIPrompt().then(resolve).catch(reject);
            });
        });
    });
  }

  // Separate method for GSI prompt handling
  async tryGSIPrompt() {
    return new Promise((resolve, reject) => {
      // console.log("Attempting GSI prompt...");

      window.google.accounts.id.prompt(notification => {
        // console.log("GSI notification:", notification);

        if (notification.isNotDisplayed()) {
          const reason = notification.getNotDisplayedReason();
          // console.log("GSI prompt không được hiển thị:", reason);

          if (reason === 'opt_out_or_no_session') {
            reject(
              new Error(
                'User has opted out or no session available. Please use OAuth2 popup.'
              )
            );
          } else {
            reject(new Error(`GSI prompt not displayed: ${reason}`));
          }
        } else if (notification.isSkippedMoment()) {
          const reason = notification.getSkippedReason();
          // console.log("GSI prompt bị skip:", reason);
          reject(new Error(`GSI prompt skipped: ${reason}`));
        } else if (notification.isDismissedMoment()) {
          const reason = notification.getDismissedReason();
          // console.log("GSI prompt bị dismiss:", reason);
          reject(new Error(`GSI prompt dismissed: ${reason}`));
        }
        // If none of the above, the prompt should be displayed and we wait for handleCredentialResponse
      });

      // Set timeout for GSI prompt
      setTimeout(() => {
        reject(new Error('GSI prompt timeout'));
      }, 10000);
    });
  }

  // New method: OAuth2 popup sign-in (more reliable than FedCM)
  async signInWithOAuth2Popup() {
    return new Promise((resolve, reject) => {
      try {
        // console.log("Initializing OAuth2 popup with scopes:", scopeString);

        const client = window.google.accounts.oauth2.initCodeClient({
          client_id: this.config.clientId,
          scope: this.config.scopes,
          ux_mode: 'popup',
          redirect_uri: window.location.origin,
          callback: async response => {
            if (response.error) {
              // console.error("OAuth2 callback error:", response.error);
              reject(new Error(response.error));
              return;
            }

            try {
              this.code = response.code;
              // console.log("Access token received, fetching user info...");
              // const userInfo = await this.fetchUserInfo();
              resolve(response);
            } catch (fetchError) {
              // console.error("Error fetching user info:", fetchError);
              reject(fetchError);
            }
          },
        });

        // Request access token with explicit prompt
        client.requestCode();
      } catch (error) {
        // console.error("OAuth2 popup initialization error:", error);
        reject(error);
      }
    });
  }

  // Fallback sign-in using OAuth2
  async signInWithOAuth2() {
    if (!window.google?.accounts?.oauth2) {
      throw new Error('OAuth2 client chưa được khởi tạo');
    }

    return new Promise((resolve, reject) => {
      try {
        // console.log("Fallback OAuth2 with scopes:", scopeString);

        const client = window.google.accounts.oauth2.initCodeClient({
          client_id: this.config.clientId,
          scope: this.config.scopes,
          redirect_uri: window.location.origin,
          callback: async response => {
            if (response.error) {
              // console.error("OAuth2 fallback error:", response.error);
              reject(new Error(response.error));
              return;
            }

            try {
              this.code = response.code;
              // Lấy thông tin user từ Google API
              // const userInfo = await this.fetchUserInfo();
              resolve(response);
            } catch (fetchError) {
              // console.error(
              //   "Error in OAuth2 fallback fetchUserInfo:",
              //   fetchError
              // );
              reject(fetchError);
            }
          },
        });

        client.requestCode();
      } catch (error) {
        // console.error("OAuth2 fallback initialization error:", error);
        reject(error);
      }
    });
  }

  // Lấy thông tin user từ Google API using access token
  async fetchUserInfo() {
    if (!this.code) {
      throw new Error('Access token không khả dụng');
    }

    const response = await fetch(
      `https://www.googleapis.com/oauth2/v2/userinfo?access_token=${this.code}`
    );
    const userData = await response.json();

    if (response.ok) {
      this.currentUser = {
        id: userData.id,
        name: userData.name,
        givenName: userData.given_name,
        familyName: userData.family_name,
        email: userData.email,
        photo: userData.picture,
        emailVerified: userData.verified_email,
      };

      return this.currentUser;
    } else {
      throw new Error(userData.error?.message || 'Failed to fetch user info');
    }
  }

  // // Render sign-in button
  // renderSignInButton(element, options = {}) {
  //   if (!this.isInitialized) {
  //     throw new Error("GoogleService chưa được khởi tạo");
  //   }

  //   const defaultOptions = {
  //     theme: "outline",
  //     size: "large",
  //     type: "standard",
  //     shape: "rectangular",
  //     text: "signin_with",
  //     logo_alignment: "left",
  //   };

  //   const buttonOptions = { ...defaultOptions, ...options };

  //   window.google.accounts.id.renderButton(element, buttonOptions);
  // }

  // Đăng xuất
  async signOut() {
    try {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.disableAutoSelect();
      }

      // Revoke access token nếu có
      if (this.code) {
        await fetch(`https://oauth2.googleapis.com/revoke?token=${this.code}`, {
          method: 'POST',
        });
      }

      // Clear user data
      this.currentUser = null;
      this.code = null;
      this.idToken = null;

      // console.log("Đăng xuất thành công");
    } catch (error) {
      // console.error("Lỗi đăng xuất:", error);
      throw new Error(`Đăng xuất thất bại: ${error.message}`);
    }
  }

  // // Kiểm tra trạng thái đăng nhập
  // isSignedIn() {
  //   return !!this.currentUser;
  // }

  // // Lấy thông tin người dùng hiện tại
  // async getCurrentUser() {
  //   if (!this.isSignedIn()) {
  //     throw new Error("Người dùng chưa đăng nhập");
  //   }

  //   return this.currentUser;
  // }

  // // Lấy access token
  // getAccessToken() {
  //   return this.code;
  // }

  // // Lấy ID token
  // getIdToken() {
  //   return this.idToken;
  // }

  // // Request additional scopes
  // async grantAdditionalScopes(scopes) {
  //   if (!this.isSignedIn()) {
  //     throw new Error("Người dùng chưa đăng nhập");
  //   }

  //   return new Promise((resolve, reject) => {
  //     try {
  //       // Ensure scopes is properly formatted
  //       let scopeString;
  //       if (Array.isArray(scopes)) {
  //         scopeString = scopes.join(" ");
  //       } else if (typeof scopes === "string") {
  //         scopeString = scopes;
  //       } else {
  //         throw new Error("Scopes must be an array or string");
  //       }

  //       const client = window.google.accounts.oauth2.initTokenClient({
  //         client_id: this.config.clientId,
  //         scope: scopeString,
  //         include_granted_scopes: true,
  //         callback: (response) => {
  //           if (response.error) {
  //             reject(new Error(response.error));
  //             return;
  //           }

  //           this.code = response.access_token;
  //           resolve(true);
  //         },
  //       });

  //       client.requestAccessToken();
  //     } catch (error) {
  //       reject(error);
  //     }
  //   });
  // }

  // // Kiểm tra quyền (simplified - GIS doesn't provide direct scope checking)
  // hasGrantedScope(scope) {
  //   // Note: GIS doesn't provide direct scope checking like the old gapi
  //   // You would need to make API calls to test if you have the required permissions
  //   // console.warn(
  //   //   "hasGrantedScope is not directly supported in GIS. Test by making API calls instead."
  //   // );
  //   return !!this.code;
  // }

  // // Pure OAuth2 fallback method (bypasses GSI completely)
  // async signInWithPureOAuth2() {
  //   const scope = this.config.scopes?.join(" ") || "openid profile email";
  //   const redirectUri = `${window.location.origin}/oauth2callback`;

  //   const authUrl = new URL("https://accounts.google.com/oauth/authorize");
  //   authUrl.searchParams.set("client_id", this.config.clientId);
  //   authUrl.searchParams.set("redirect_uri", redirectUri);
  //   authUrl.searchParams.set("response_type", "code");
  //   authUrl.searchParams.set("scope", scope);
  //   authUrl.searchParams.set("access_type", "offline");
  //   authUrl.searchParams.set("prompt", "consent");

  //   // Open popup window
  //   const popup = window.open(
  //     authUrl.toString(),
  //     "google-auth",
  //     "width=500,height=600,scrollbars=yes,resizable=yes"
  //   );

  //   return new Promise((resolve, reject) => {
  //     const checkClosed = setInterval(() => {
  //       if (popup.closed) {
  //         clearInterval(checkClosed);
  //         reject(new Error("Authentication cancelled by user"));
  //       }
  //     }, 1000);

  //     // Listen for messages from popup
  //     const messageListener = (event) => {
  //       if (event.origin !== window.location.origin) return;

  //       if (event.data.type === "GOOGLE_AUTH_SUCCESS") {
  //         clearInterval(checkClosed);
  //         window.removeEventListener("message", messageListener);
  //         popup.close();

  //         // Exchange code for tokens (you'll need a backend endpoint for this)
  //         this.exchangeCodeForTokens(event.data.code)
  //           .then(resolve)
  //           .catch(reject);
  //       } else if (event.data.type === "GOOGLE_AUTH_ERROR") {
  //         clearInterval(checkClosed);
  //         window.removeEventListener("message", messageListener);
  //         popup.close();
  //         reject(new Error(event.data.error));
  //       }
  //     };

  //     window.addEventListener("message", messageListener);
  //   });
  // }

  // // Exchange authorization code for tokens (requires backend)
  // async exchangeCodeForTokens(code) {
  //   // This would typically be done on your backend server
  //   // For security reasons, never expose your client secret in frontend code
  //   throw new Error(
  //     "Token exchange must be implemented on your backend server"
  //   );
  // }

  // // Cấu hình lại service
  // updateConfig(newConfig) {
  //   this.config = { ...this.config, ...newConfig };

  //   // Ensure scopes is always an array
  //   if (this.config.scopes && typeof this.config.scopes === "string") {
  //     this.config.scopes = this.config.scopes
  //       .split(" ")
  //       .filter((scope) => scope.trim());
  //   }

  //   // console.log("Updated config:", {
  //   //   clientId: this.config.clientId
  //   //     ? this.config.clientId.substring(0, 20) + "..."
  //   //     : "MISSING",
  //   //   scopes: this.config.scopes,
  //   //   apiKey: this.config.apiKey ? "***" : "MISSING",
  //   // });
  // }

  // // Reload/reinitialize service
  // async reload() {
  //   this.isInitialized = false;
  //   this.currentUser = null;
  //   this.code = null;
  //   this.idToken = null;
  //   return this.init();
  // }
}

// Tạo instance singleton
const googleService = new GoogleService();

export default googleService;
